import React, { useState, createContext, useRef, useCallback, useMemo } from 'react';

export const WidgetContext = createContext();

// Shared identity so an already-idle clear can bail out without re-rendering every consumer.
const EMPTY_KEYS = Object.freeze([]);
const IDLE_SAVE = { inFlight: false, failed: false };

// Which widget acks a page save still awaits; keyed not counted, so an unrelated event can't drain it.
export const WidgetContextProvider = ({ children }) => {
	const [pendingWidgetKeys, setPendingWidgetKeys] = useState(EMPTY_KEYS);
	// The creates this save submitted, kept whole while pendingWidgetKeys drains: the order PATCH
	// must not run until each of these has had its server id merged in.
	const [submittedCreateIds, setSubmittedCreateIds] = useState(EMPTY_KEYS);
	const [saveState, setSaveState] = useState(IDLE_SAVE);
	// Mirrors pendingWidgetKeys so membership can be checked synchronously, outside a state updater.
	const pendingKeysRef = useRef(EMPTY_KEYS);

	// device preview container ref used to get background image height
	const previewerRef = useRef();

	const beginPendingWidgets = useCallback(dirtyWidgets => {
		pendingKeysRef.current = dirtyWidgets.map(widget => widget.id || widget.clientId);
		setPendingWidgetKeys(pendingKeysRef.current);
		setSubmittedCreateIds(dirtyWidgets.filter(widget => !widget.id).map(widget => widget.clientId));
		setSaveState({ inFlight: true, failed: false });
	}, []);

	// The failure flag is gated on membership too: an error ack from an abandoned save must not fail
	// the save open now, or we skip its widget_order PATCH and orphan the widgets it just created.
	const resolvePendingWidget = useCallback((key, { failed = false } = {}) => {
		if (!pendingKeysRef.current.includes(key)) return;
		pendingKeysRef.current = pendingKeysRef.current.filter(pendingKey => pendingKey !== key);
		setPendingWidgetKeys(pendingKeysRef.current);
		if (failed) setSaveState(currentState => ({ ...currentState, failed: true }));
	}, []);

	// Called on every page change and unmount, so it no-ops when already idle rather than
	// re-rendering every consumer of this context.
	const clearPendingWidgets = useCallback(() => {
		pendingKeysRef.current = EMPTY_KEYS;
		setPendingWidgetKeys(currentKeys => (currentKeys.length ? EMPTY_KEYS : currentKeys));
		setSubmittedCreateIds(currentIds => (currentIds.length ? EMPTY_KEYS : currentIds));
		setSaveState(currentState =>
			currentState.inFlight || currentState.failed ? IDLE_SAVE : currentState
		);
	}, []);

	// Editor and WidgetDevicePreview read previewerRef from here; don't re-render them on every
	// re-render above this provider.
	const value = useMemo(
		() => ({
			pendingWidgetKeys,
			submittedCreateIds,
			saveState,
			beginPendingWidgets,
			resolvePendingWidget,
			clearPendingWidgets,
			previewerRef,
		}),
		[
			pendingWidgetKeys,
			submittedCreateIds,
			saveState,
			beginPendingWidgets,
			resolvePendingWidget,
			clearPendingWidgets,
		]
	);

	return <WidgetContext.Provider value={value}>{children}</WidgetContext.Provider>;
};
